import { useCallback, useEffect, useLayoutEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { flushSync } from 'react-dom'
import { useCommands } from '../../context/CommandContext'
import { useCommandShortcut } from '../../hooks/useCommandShortcut'
import { useI18n } from '../../i18n/I18nContext'
import { searchCommands, type CommandResult } from '../../services/commandRegistry'
import '../../styles/commandPalette.css'

function PaletteDialog({ execute, error, results, query, setQuery, close }: {
  execute: (result: CommandResult) => void; error: boolean; results: CommandResult[]; query: string; setQuery: (query: string) => void; close: () => void
}) {
  const { t } = useI18n()
  const dialog = useRef<HTMLDialogElement>(null), input = useRef<HTMLInputElement>(null)
  const id = useId()
  // Keep the original opener across Strict Mode layout-effect cleanup/replay.
  const returnFocus = useRef(typeof document !== 'undefined' && document.activeElement instanceof HTMLElement ? document.activeElement : null)
  const [selected, setSelected] = useState(0)
  const index = Math.min(selected, Math.max(0, results.length - 1))
  useLayoutEffect(() => {
    const element = dialog.current!
    element.showModal()
    input.current?.focus()
    return () => {
      element.close()
      if (returnFocus.current?.isConnected) returnFocus.current.focus()
    }
  }, [])
  useEffect(() => {
    document.getElementById(`${id}-option-${index}`)?.scrollIntoView({ block: 'nearest' })
  }, [id, index, results])
  return <dialog ref={dialog} className="command-palette" aria-modal="true" aria-labelledby={`${id}-title`}
    onCancel={event => { event.preventDefault(); close() }}
    onClick={event => { if (event.target === event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect()
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close()
    } }}
    onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close() }
      if (event.key === 'Tab') {
        const first = dialog.current?.querySelector('button')
        if (!event.shiftKey && document.activeElement === input.current) {
          event.preventDefault(); first?.focus()
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); input.current?.focus()
        }
      }
    }}>
    <header className="command-palette__header">
      <h2 id={`${id}-title`}>{t('Command palette')}</h2>
      <button className="btn btn-ghost" type="button" onClick={close}>{t('Close')}</button>
    </header>
    <label className="sr-only" htmlFor={`${id}-search`}>{t('Search commands')}</label>
    <input ref={input} id={`${id}-search`} className="input" role="combobox" aria-autocomplete="list"
      aria-expanded="true" aria-controls={`${id}-results`} aria-describedby={`${id}-help`}
      aria-activedescendant={results[index] ? `${id}-option-${index}` : undefined}
      autoComplete="off" value={query} placeholder={t('Search commands')}
      onChange={event => { setQuery(event.target.value); setSelected(0) }}
      onKeyDown={event => {
        if (event.nativeEvent.isComposing) return
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
          event.preventDefault()
          if (!results.length) return
          setSelected((index + (event.key === 'ArrowDown' ? 1 : -1) + results.length) % results.length)
        }
        if (event.key === 'Enter') { event.preventDefault(); if (results[index]) execute(results[index]) }
      }} />
    <p id={`${id}-help`} className="text-muted">{t('Use arrow keys to select, Enter to run, Escape to close.')}</p>
    <p role="status" aria-live="polite" aria-atomic="true">
      {results.length ? `${t('Commands available')}: ${results.length}`
        : query.trim() ? t('No matching commands. Try another search.') : t('No commands available in this workspace.')}
    </p>
    {error ? <p role="alert">{t('Unable to run this command. Please try again.')}</p> : null}
    <div id={`${id}-results`} role="listbox" aria-label={t('Commands')} className="command-palette__results">
      {Array.from(new Set(results.map(result => result.category))).map(category => <div role="group" aria-label={category} key={category}>
        <div className="command-palette__category" aria-hidden="true">{category}</div>
        {results.map((result, resultIndex) => result.category === category ? <div key={result.command.id}
          id={`${id}-option-${resultIndex}`} role="option" aria-selected={resultIndex === index}
          aria-description={result.recentlyUsed ? t('Recently used') : undefined}
          className="command-palette__option" onMouseDown={event => event.preventDefault()}
          onClick={() => execute(result)} onMouseMove={() => setSelected(resultIndex)}>
          <span>{result.label}{result.description ? <small>{result.description}</small> : null}</span>
          {result.recentlyUsed ? <small aria-hidden="true" className="helper">{t('Recently used')}</small> : null}
          {result.command.shortcut ? <kbd>{result.command.shortcut}</kbd> : null}
        </div> : null)}
      </div>)}
    </div>
  </dialog>
}

function PaletteControl() {
  const { registry, context, history } = useCommands()
  const { t } = useI18n()
  const [open, setOpen] = useState(false), [query, setQuery] = useState('')
  const [error, setError] = useState(false)
  const [asyncError, setAsyncError] = useState(false)
  const mounted = useRef(false)
  const execution = useRef(0)
  useLayoutEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const execute = (result: CommandResult) => {
    const attempt = ++execution.current
    const completed = (success: boolean) => {
      if (mounted.current && execution.current === attempt && !success) { setError(true); setOpen(true) }
    }
    try {
      // Commit dialog cleanup/focus restoration before a handler focuses page controls.
      const outcome = registry.execute(result.command, () => flushSync(() => setOpen(false)))
      if (outcome instanceof Promise) void outcome.catch(() => {
        if (mounted.current && execution.current === attempt) setAsyncError(true)
      })
      else completed(outcome)
    } catch { completed(false) }
  }
  const commands = useSyncExternalStore(registry.subscribe, registry.getSnapshot, registry.getSnapshot)
  const recents = useSyncExternalStore(history.subscribe, history.getSnapshot, history.getSnapshot)
  const results = useMemo(() => {
    const ranked = context ? searchCommands(commands, context, query, t, recents.commands) : []
    return Array.from(new Set(ranked.map(result => result.category)))
      .flatMap(category => ranked.filter(result => result.category === category))
  }, [commands, context, query, t, recents.commands])
  const show = useCallback(() => { setQuery(''); setError(false); setAsyncError(false); setOpen(true) }, [])
  useCommandShortcut(Boolean(context), show)
  const shortcut = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl+K'
  return <>
    <button type="button" className="btn btn-ghost" disabled={!context} onClick={show}
      aria-haspopup="dialog" aria-keyshortcuts="Control+k Meta+k" data-testid="command-palette-trigger">
      {t('Commands')} <kbd>{shortcut}</kbd>
    </button>
    {asyncError ? <span role="alert">{t('Unable to run this command. Please try again.')}</span> : null}
    {history.enabled && !recents.persisted ? <span role="status" className="helper">{t('Recent commands could not be saved in this browser.')}</span> : null}
    {open && context ? <PaletteDialog execute={execute} error={error} results={results} query={query} setQuery={setQuery} close={() => setOpen(false)} /> : null}
  </>
}

/** Retire open/search/selection state with the registry, without remounting workspaces. */
export function CommandPalette() {
  const { registry } = useCommands()
  const identity = useMemo(() => ({ id: ++paletteSequence }), [registry])
  return <PaletteControl key={identity.id} />
}
let paletteSequence = 0
